/**
 * kör-69 #19 (2a): route-szintű szerep-guard a gépkategória-adminhoz.
 *
 * ⚠️ EZ UI-LÁTHATÓSÁG, NEM HOZZÁFÉRÉS. A valódi kontroll a BE-n van
 * (`@Roles('BOLTVEZETO')` a POST/PATCH és a `HOURLY_RATE_*` utakon) — a guard
 * nélkül is fail-closed. Itt csak azt kerüljük el, hogy a felhasználó olyan
 * űrlapot töltsön ki, amit a szerver úgyis elutasít. (🚦 + 🔷 ruling 2026-09-07.)
 *
 * 🔴 A CSAPDA, amit ez a komponens ELKERÜL — és amit a spec-je szerint mérni kell:
 * a FE-n a szerep neve `'STORE_MANAGER'`, NEM `'BOLTVEZETO'`. A `useAuth()` a
 * `resolveDashboardRole()`-on át adja vissza (`backend-role-map`: BOLTVEZETO →
 * STORE_MANAGER). Aki `user.role === 'BOLTVEZETO'`-t ír, MINDIG `false`-ot kap,
 * és a gomb pont a jogosult szerep elől tűnik el — némán. Ez egyszer már
 * megtörtént (QA-100, a régi `as UserRole` cast). A `.spec` negatív esete
 * ezért kifejezetten a `'BOLTVEZETO'` literállal próbál, nem általános
 * nem-jogosult szereppel: egy általános neg. teszt akkor is zöld, ha a guard
 * mindent tilt.
 *
 * ⚠️ Két külön `UserRole` típus él a fában, részben átfedő literálokkal
 * (`api/users.ts` = BE-szerepek · `widget-registry` = dashboard-szerepek).
 * Ez az `allowedRoles` a DASHBOARD-oldali típusra megy — a `getAccessLevel`
 * azt várja.
 */
import { getAccessLevel } from '@/features/dashboard/hooks/useVisibleWidgets';
import type { UserRole } from '@/features/dashboard/lib/widget-registry';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@kgc/i18n';
import type { ReactNode } from 'react';

interface RequireDashboardRoleProps {
  allowedRoles: UserRole[];
  children: ReactNode;
}

export function RequireDashboardRole({ allowedRoles, children }: RequireDashboardRoleProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  // A `role === null` ág a `getAccessLevel`-ben él (QA-100 fail-closed) — nem
  // írjuk felül, csak átadjuk neki a `null`-t is.
  const accessLevel = getAccessLevel(user?.role ?? null, allowedRoles);

  if (accessLevel !== 'FULL') {
    return (
      <div className="min-h-screen kgc-bg p-6" data-testid="require-role-denied">
        <div className="max-w-4xl mx-auto">
          <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-600 text-amber-800 dark:text-amber-300 text-sm">
            {t('common.roleRequired')}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
